<template>
  <section id="services" class="padding-x mb-20">
    <div class="flex w-full flex-col">
      <h3
        id="what-i-do"
        v-html="whatIDo"
        class="heading-1 font-extrabold uppercase"
      ></h3>

      <div
        id="services-text"
        class="grid-gap text-flax-smoke-300 mt-[5%] grid grid-cols-12 justify-end opacity-0 lg:grid"
      >
        <p
          class="heading-6 text-flax-smoke-300/85 col-span-4 col-start-0 text-center text-nowrap md:col-start-4"
        >
          ( FEATURES )
        </p>
        <p
          class="heading-4 font-fancy col-span-8 w-full text-balance sm:font-semibold md:col-span-5"
        >
          Scaling a platform doesn't happen by chance, it's built with
          intention. We provide the zero-upfront liquidity and high-performance
          tech that makes your operator journey effortless.
        </p>
      </div>
    </div>

    <div class="relative mt-12 w-full lg:mt-[10%]">
      <div class="mt-12 flex flex-col justify-between gap-y-16">
        <ServicesCard
          v-for="(card, index) in servicesCardProps"
          :key="index"
          :number="index + 1"
          :title="card.title"
          :body="card.body"
          :headings="card.headings"
          :shape="card.shape"
          :id="card.id"
          @toggle-infographic="handleToggle"
          class="border-flax-smoke-500/50 sticky border-t bg-flax-smoke-700"
          :class="getStyle(index)"
        />
      </div>
    </div>

    <!-- Modals / Infographic Overlays -->
    <Teleport to="body">
      <div v-if="activeInfographic" class="fixed inset-0 z-100000 flex items-center justify-center p-4 sm:p-10">
        <div @click="activeInfographic = null" class="absolute inset-0 bg-black/90 backdrop-blur-md"></div>
        <div class="relative max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-3xl border border-white/10 bg-[#0d0c1d] p-4 shadow-2xl md:p-10">
          <button @click="activeInfographic = null" class="absolute right-6 top-6 z-10 text-white/50 hover:text-white">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
          
          <GGRInfographic v-if="activeInfographic === 'ggr'" />
          <LoyaltyInfographic v-if="activeInfographic === 'loyalty'" />
        </div>
      </div>
    </Teleport>
  </section>
</template>

<script setup lang="ts">
  import { animateSplitText } from '@/animations';
  import { textSplitterIntoChar } from '@/functions';
  import { onBeforeMount, onMounted, ref } from 'vue';
  import ServicesCard from '../ServicesCard.vue';
  import GGRInfographic from '../infographics/GGRInfographic.vue';
  import LoyaltyInfographic from '../infographics/LoyaltyInfographic.vue';

  const whatIDo = ref('Platform Features /');

  const activeInfographic = ref<string | null>(null);
  const handleToggle = (id: string) => {
    activeInfographic.value = id;
  };

  const servicesCardProps = [
    {
      id: 'ggr',
      title: 'GGR Advantage',
      body: 'We provide the bankroll and the platform. You operate with 0% upfront costs and handle settlements a week behind, ensuring your cash flow remains positive from day one.',
      headings: ['Zero Upfront Cost', 'Weekly Settlements', 'Platform Liquidity'],
      shape: `<svg viewBox="0 0 200 200"  xmlns="http://www.w3.org/2000/svg">
  <path d="M100 0C103.395 53.7596 146.24 96.6052 200 100C146.24 103.395 103.395 146.24 100 200C96.6052 146.24 53.7596 103.395 0 100C53.7596 96.6052 96.6052 53.7596 100 0Z"/>
  </svg>
  `,
    },
    {
      id: 'loyalty',
      title: 'Loyalty Engine',
      body: 'Our advanced retention tools keep players engaged. From automated bonus systems to personalized player journeys, we provide the tech to maximize Lifetime Value (LTV).',
      headings: [
        'Automated Bonuses',
        'Player Segmentation',
        'Retention Automations',
      ],
      shape: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
  <path d="M127.14 200C99.9942 200 99.9943 167.423 72.8487 167.423C41.6048 167.423 0 158.386 0 127.133C0 99.9885 32.5678 99.9885 32.5678 72.8445C32.5678 41.6139 41.6048 0 72.8602 0C100.006 0 100.006 32.5774 127.151 32.5774C158.384 32.5774 200 41.6139 200 72.8675C200 100.012 167.421 100.012 167.421 127.156C167.409 158.444 158.384 200 127.14 200Z" />
  </svg>
  `,
    },
    {
      title: 'Partner Program',
      body: 'Scale your operation with our comprehensive agent and affiliate management system. Real-time reporting and multi-tier commission structures built for growth.',
      headings: [
        'Agent Management',
        'Affiliate Tracking',
        'Real-time Analytics',
      ],
      shape: `<svg  viewBox="0 0 200 200"  xmlns="http://www.w3.org/2000/svg">
  <path d="M100 0L170.711 29.2893L200 100L170.711 170.711L100 200L29.2893 170.711L0 100L29.2893 29.2893L100 0Z" />
  </svg>
  `,
    },
  ];

  onBeforeMount(() => {
    whatIDo.value = textSplitterIntoChar('Platform Features /', true);
  });

  onMounted(() => {
    animateSplitText('#what-i-do .letters', '#services-text', 0.7, 0.01, 0);
  });

  const getStyle = (index: number) => {
    if (index === 0) {
      return 'top-[calc(20vh_+_0em)] mb-[17.25em]';
    }

    if (index === 1) {
      return 'top-[calc(20vh_+_5.75em)] mb-[11.5em]';
    }

    if (index === 2) {
      return 'top-[calc(20vh_+_11.5em)] mb-[5.75em]';
    }
  };
</script>
